/* eslint-disable no-unused-vars */
import { mergeArraysOptimized } from "@/lib";
import { createSlice } from "@reduxjs/toolkit";

export const dailySlice = createSlice({
  name: "dailyReport",
  initialState: {
    dailyform: [],
    uploadSaved: [],
    updatePreviousmeta: [],
    previousValidationData: null,
    singleEventdata: null,
    updateEvent: null,
    temporalEvent: null,
    previousEvents: null,
    tankdetails: { data: null, isupdated: false },
    bunkerdropdownarr: {},
    monthlydraft: [],
    singlemonthlydraft: null,
  },

  reducers: {
    fetchMonthlyDraftByDate: (state, action) => {
      if (!action.payload) {
        state.singlemonthlydraft = null;
      } else {
        const reportTodo = JSON.parse(JSON.stringify(state.monthlydraft));
        const getData = reportTodo?.find((x) => x.date === action.payload);
        state.singlemonthlydraft = getData;
      }
    },
    deleteMonthly: (state, action) => {
      const reportTodo = JSON.parse(JSON.stringify(state.monthlydraft));

      state.monthlydraft = reportTodo.map((item) => {
        if (item?.date === action.payload) {
          return {
            ...item,
            isdeleted: 1,
          };
        }
        return item;
      });
    },
    saveMonthlyDraft: (state, action) => {
      const { draftData, date, eventType, formType, imo_number, type } =
        action.payload;

      // Initialize monthlydraft if not present
      if (!state.monthlydraft) {
        state.monthlydraft = [];
      }

      // Find existing entry by date
      const existingIndex = state.monthlydraft.findIndex(
        (draft) => draft.date === date
      );

      // Normalize incoming data to tablename/data structure
      const normalizedDraftData = draftData.reduce((acc, obj) => {
        const key = Object.keys(obj)[0];
        acc[key] = obj[key];
        return acc;
      }, {});

      if (existingIndex !== -1) {
        const existingDraft = state.monthlydraft[existingIndex];

        // Normalize existing data
        const existingData = existingDraft.data.reduce((acc, obj) => {
          acc[obj.tablename] = obj.data;
          return acc;
        }, {});

        // Merge normalized data
        const merged = { ...existingData, ...normalizedDraftData };

        // Convert to new array format
        const mergedDataArray = Object.entries(merged).map(([key, value]) => ({
          tablename: key,
          data: value,
        }));

        // Update the existing draft
        state.monthlydraft[existingIndex] = {
          ...existingDraft,
          data: mergedDataArray,
          eventType: eventType || existingDraft.eventType,
          imo_number: imo_number || existingDraft.imo_number,
          isActive: false,
          type: type,
          formType: formType,
          isdeleted: false,
        };
      } else {
        // Create new draft with formatted data
        const formattedData = Object.entries(normalizedDraftData).map(
          ([key, value]) => ({
            tablename: key,
            data: value,
          })
        );

        const newDraftObject = {
          data: formattedData,
          eventType,
          date,
          imo_number,
          type: type,
          isActive: false,
          formType: formType,
          isdeleted: false,
          createdAt: new Date().toISOString(),
        };

        state.monthlydraft.push(newDraftObject);
      }
    },

    restoreSlice: (state, action) => {
      const newState = action.payload;
      const dailyReportKeys = [
        "dailyform",
        "uploadSaved",
        "updatePreviousmeta",
        "previousValidationData",
        "singleEventdata",
        "updateEvent",
        "temporalEvent",
        "previousEvents",
        "bunkerdropdownarr",
      ];

      dailyReportKeys.forEach((key) => {
        if (newState[key] !== undefined) {
          state[key] = newState[key];
        }
      });

      if (newState.tankdetails) {
        state.tankdetails = {
          ...state.tankdetails,
          data: newState.tankdetails.data ?? state.tankdetails.data,
          isupdated:
            newState.tankdetails.isupdated ?? state.tankdetails.isupdated,
        };
      }
    },

    addBunkerDropdown: (state, action) => {
      const bunkerArrdata = JSON.parse(
        JSON.stringify(state.bunkerdropdownarr || {})
      );
      Object.entries(action.payload).forEach(([key, newItems]) => {
        if (!bunkerArrdata[key]) {
          bunkerArrdata[key] = [];
        }

        bunkerArrdata[key] = [
          ...bunkerArrdata[key].filter(
            (existingItem) =>
              !newItems.some((newItem) => existingItem.value === newItem.value)
          ),
          ...newItems,
        ];
      });
      state.bunkerdropdownarr = bunkerArrdata;
    },
    updateBunkerDropdown: (state, action) => {
      const { key, label, capacity, active } = action.payload;
      const bunkerArrdata = JSON.parse(
        JSON.stringify(state.bunkerdropdownarr || {})
      );

      if (bunkerArrdata[key]) {
        bunkerArrdata[key] = bunkerArrdata[key].map((item) =>
          item.label === label ? { ...item, capacity, active } : item
        );
      }

      state.bunkerdropdownarr = bunkerArrdata;
    },

    removeBunkerDropdownItem: (state, action) => {
      Object.entries(action.payload).forEach(([key, newItems]) => {
        if (state.bunkerdropdownarr[key]) {
          if (newItems?.length > 0) {
            state.bunkerdropdownarr[key] = newItems;
          } else {
            state.bunkerdropdownarr[key] = [];
          }
        } else {
          if (newItems?.length > 0) {
            state.bunkerdropdownarr[key] = newItems;
          } else {
            state.bunkerdropdownarr[key] = [];
          }
        }
      });
    },

    addReport: (state, action) => {
      const {
        datetime,
        eventtype,
        data,
        usertype,
        currentStep,
        reportType,
        type,
        username,
        isupdating,
        ship_id,
      } = action.payload;
      if (!data?.tablename) {
        console.warn("No tablename provided, skipping report addition.");
        return;
      }
      const reportTodo = JSON.parse(JSON.stringify(state.dailyform));

      if (reportTodo?.length) {
        const existingIndex = reportTodo.findIndex(
          (item) => item?.datetime == datetime && item?.ship_id == ship_id
        );

        if (existingIndex !== -1) {
          const existingEntry = reportTodo[existingIndex];
          existingEntry.currentStep = currentStep;

          const tablenameIndex = existingEntry.data.findIndex(
            (entry) => entry.tablename === data?.tablename
          );

          if (tablenameIndex !== -1) {
            existingEntry.data[tablenameIndex] = data;
            existingEntry.usertype = usertype;
          } else {
            existingEntry.data.push(data);
          }
        } else {
          const newEntry = {
            datetime,
            eventtype,
            username,
            usertype,
            reportType,
            ship_id,
            type,
            isdeleted: 0,
            isupdating,
            data: [data],
            currentStep: currentStep,
          };
          reportTodo.push(newEntry);
        }
        state.dailyform = reportTodo;
      } else {
        const newEntry = {
          datetime,
          eventtype,
          ship_id,
          usertype,
          type,
          reportType,
          data: [data],
          username,
          isupdating,
          isdeleted: 0,
          currentStep,
        };
        state.dailyform = [newEntry];
      }
    },

    saveTempPreviousData: (state, action) => {
      const updatedData = action.payload;

      if (!state.previousValidationData) {
        state.previousValidationData = {};
      }

      Object.keys(updatedData).forEach((category) => {
        if (typeof updatedData[category] === "object") {
          if (!state.previousValidationData[category]) {
            state.previousValidationData[category] = {};
          }

          Object.keys(updatedData[category]).forEach((key) => {
            if (updatedData[category][key] !== undefined) {
              state.previousValidationData[category][key] =
                updatedData[category][key];
            }
          });
        }
      });
    },

    uploadReport: (state, action) => {
      const {
        datetime,
        eventtype,
        data,
        currentStep,
        reportType,
        username,
        ship_id,
      } = action.payload;
      const newEntry = {
        datetime,
        eventtype,
        ship_id,
        data: data,
        username,
        reportType,
        isdeleted: 0,
        currentStep,
      };
      state.dailyform = [newEntry];
    },
    onuploadedSaveRecords: (state, action) => {
      const uploadSaved = state.uploadSaved || [];
      uploadSaved.push(action?.payload);
      state.uploadSaved = uploadSaved;
    },

    deleteTour: (state, action) => {
      const tourtype = action.payload;
      console.log("tourtype", tourtype);
      if (tourtype === "DAILY") {
        let reportTodo = JSON.parse(JSON.stringify(state.dailyform));
        state.dailyform = reportTodo.filter((item) => {
          return item?.type !== "TOUR";
        });
      } else {
        let reportTodo = JSON.parse(JSON.stringify(state.monthlydraft));
        state.monthlydraft = reportTodo.filter((item) => {
          return item?.type !== "TOUR";
        });
      }
    },
    deleteTodo: (state, action) => {
      const { datetime, type } = action.payload;
      const reportTodo = JSON.parse(JSON.stringify(state.dailyform));

      if (type === "HARD") {
        state.dailyform = reportTodo.filter((item) => {
          return item?.datetime !== datetime;
        });
      } else if (type === "SOFT") {
        state.dailyform = reportTodo.map((item) => {
          if (item?.datetime === datetime) {
            return {
              ...item,
              isdeleted: 1,
            };
          }
          return item;
        });
      }
    },

    updateNextEvent: (state, action) => {
      state.updateEvent = action.payload;
    },

    updatePreviousstate: (state, action) => {
      const newRecord = action.payload;
      if (!state.updatePreviousmeta) {
        state.updatePreviousmeta = [];
      }

      const existingIndex = state.updatePreviousmeta.findIndex(
        (item) => item.tablename === newRecord.tablename
      );

      if (existingIndex >= 0) {
        state.updatePreviousmeta[existingIndex].date = newRecord.date;
      } else {
        state.updatePreviousmeta.push(newRecord);
      }
    },
    updatePrevioustimestamp: (state, action) => {
      const newRecord = action.payload;
      const timstampInfo = JSON.parse(JSON.stringify(state.updatePreviousmeta));

      const existingIndex = timstampInfo.findIndex(
        (item) => item.tablename === newRecord?.tablename
      );
      if (existingIndex >= 0) {
        timstampInfo[existingIndex] = {
          ...timstampInfo[existingIndex],
          ...newRecord,
        };
      }
      state.updatePreviousmeta = timstampInfo;
    },

    updateTemporalEvent: (state, action) => {
      const { tempevent, previousEvent } = action.payload;
      state.temporalEvent = tempevent;
      state.previousEvents = previousEvent;
    },
    // eslint-disable-next-line no-unused-vars

    clearTemporalEvent: (state, action) => {
      state.temporalEvent = null;
      state.previousEvents = null;
    },
    getById: (state, action) => {
      const reportTodo = JSON.parse(JSON.stringify(state.dailyform));
      const getData = reportTodo?.find((x) => x.datetime === action.payload);
      state.singleEventdata = getData;
    },
    patchuserData: (state, action) => {
      const parsedPayload = {
        ...action.payload,
        data: action.payload.data.map((entry) => {
          const parsedData = { ...entry.data };

          Object.keys(parsedData).forEach((key) => {
            const value = parsedData[key];

            if (typeof value === "string" && value.trim().startsWith("[")) {
              try {
                parsedData[key] = JSON.parse(value);
              } catch (e) {
                // If JSON.parse fails, keep the value as is
              }
            }
          });

          return {
            ...entry,
            data: parsedData,
          };
        }),
      };

      console.log("action?.payload", parsedPayload);
      state.singleEventdata = parsedPayload;
    },
    getByIdOnline: (state, action) => {
      state.singleEventdata = action?.payload;

      const realReportlist = Array.isArray(action?.payload)
        ? state.dailyform.filter((x) => x !== null)
        : [];
      const reportTodo = JSON.parse(JSON.stringify(realReportlist)) || [];

      if (reportTodo.length) {
        const getDataIndex = reportTodo.findIndex(
          (x) => x?.draft_id === action?.payload?.draft_id
        );

        if (getDataIndex !== -1) {
          reportTodo[getDataIndex].data = mergeArraysOptimized(
            Array.isArray(reportTodo[getDataIndex].data)
              ? reportTodo[getDataIndex].data
              : [reportTodo[getDataIndex].data],
            Array.isArray(action.payload?.data)
              ? action.payload.data
              : [action.payload.data]
          );

          state.dailyform = reportTodo;
          state.singleEventdata = reportTodo[getDataIndex];
        } else {
          state.dailyform = [...reportTodo, action.payload];
          state.singleEventdata = action.payload;
        }
      } else {
        state.dailyform = [action.payload];
        state.singleEventdata = action.payload;
      }
    },

    getClearData: (state, action) => {
      state.singleEventdata = null;
    },
    clearDraft: (state, action) => {
      state.dailyform = [];
    },
    updateTankDetails: (state, action) => {
      state.tankdetails = {
        data: action.payload?.data,
        isupdated: action.payload?.isupdated,
      };
    },
  },
});

export const {
  addReport,
  deleteTodo,
  uploadReport,
  getById,
  getClearData,
  getByIdOnline,
  updateNextEvent,
  updateTemporalEvent,
  clearTemporalEvent,
  onuploadedSaveRecords,
  updatePreviousstate,
  clearDraft,
  updatePrevioustimestamp,
  saveTempPreviousData,
  updateTankDetails,
  addBunkerDropdown,
  removeBunkerDropdownItem,
  updateBunkerDropdown,
  restoreSlice,
  patchuserData,
  saveMonthlyDraft,
  fetchMonthlyDraftByDate,
  deleteMonthly,
  deleteTour,
} = dailySlice.actions;

export default dailySlice.reducer;
